import AccountSettingsHeader from '../../../../components/account/settings/AccountSettingsHeader.tsx';
import AccountLayout from '../../../../components/layouts/AccountLayout.tsx';
import { useParams } from 'react-router-dom';
import { useContestAttempts, useContestDetail } from '../../../../components/contests/contestsQueries.ts';
import DateHelper from '../../../../helpers/dateHelper.ts';
import { PulseLoader } from '../../../../components/loaders/PulseLoader.tsx';
import { useModal } from '../../../../hooks/useModal.tsx';
import ButtonRounded from '../../../../components/buttons/ButtonRounded.tsx';
import ContestVerifyAttemptModalContent
    from '../../../../components/contests/widgets/ContestVerifyAttemptModalContent.tsx';
import { ContestAttemptStatusBadge } from '../../../../components/contests/widgets/ContestAttemptStatusBadge.tsx';
import { useQueryClient } from '@tanstack/react-query';

function AccountManagementContestsDetailPage() {
    const { id } = useParams();
    const { showModal } = useModal();
    const queryClient = useQueryClient();

    const contestDetailsResult = useContestDetail(id ?? '');
    const contestAttemptsResult = useContestAttempts(id ?? '');
    const stats = [
        { name: 'Rozpoczętych', value: contestDetailsResult.data?.data.attemptsCount },
        {
            name: 'Ukończonych',
            value: contestDetailsResult.data?.data.completedAttemptsCount,
        },
        { name: 'Do weryfikacji', value: contestDetailsResult.data?.data.pendingReviewAttemptsCount, verifyBtn: true },
    ];

    const statuses = { Completed: 'text-green-400 bg-green-400/10', Error: 'text-rose-400 bg-rose-400/10' };

    function classNames(...classes: string[]) {
        return classes.filter(Boolean).join(' ');
    }

    let isActive = false;
    if (contestDetailsResult.isSuccess) {
        isActive = (DateHelper.GetDateTimeNowAsUnixTimespan() > DateHelper.DateTimeToUnixTimestamp(new Date(contestDetailsResult.data?.data.startsAt))) &&
            (DateHelper.GetDateTimeNowAsUnixTimespan() < DateHelper.DateTimeToUnixTimestamp(new Date(contestDetailsResult.data?.data.endsAt)));
    }

    const modalHeader = () =>
        <div>
            {/*<p className={'text-neutral-400 font-medium'}>Weryfikacja</p>*/}
            {/*<h1 className={"text-2xl"}>{name}</h1>*/}
            <h1 className={'text-2xl line-clamp-2'}>Weryfikacja zgłoszenia</h1>
        </div>;

    const showVerifyModal = (attemptId?: string) => {
        showModal(modalHeader(), <ContestVerifyAttemptModalContent contestId={id!}
                                                                   attemptId={attemptId} />, undefined, 1100, null, () => {
            queryClient.invalidateQueries({ queryKey: ['contests', id, 'details'], exact: true });
            queryClient.invalidateQueries({ queryKey: ['contests', id, 'attempts'], exact: true });
        });
    };


    return (
        <AccountLayout padding={''}>
            <div className={'px-2'}>
                <AccountSettingsHeader title={'Szczegóły konkursu'} backButtonIsVisible />
            </div>
            <main>
                {contestDetailsResult.isSuccess &&
                    <header>
                        <div
                            className="flex flex-col items-start justify-between gap-x-8 gap-y-4 bg-gray-950/70 px-4 py-4 sm:flex-row sm:items-center sm:px-6 lg:px-8">
                            <div>
                                <div className="flex items-center gap-x-3">
                                    <div
                                        className={classNames(statuses[isActive ? 'Completed' : 'Error'], 'flex-none rounded-full')}>
                                        <div className="size-2 rounded-full bg-current" />
                                    </div>
                                    <h1 className="flex gap-x-3 text-xl/7">
                                        <span
                                            className="font-semibold text-white">{contestDetailsResult.data.data.title}</span>
                                        {/*<span className="text-gray-600">/</span>*/}
                                        {/*<span className="font-semibold text-white">mobile-api</span>*/}
                                    </h1>
                                </div>
                                {/*<p className="mt-2 text-xs/6 text-gray-400">Deploys from GitHub via main branch</p>*/}
                            </div>
                            {/*<div*/}
                            {/*    className="order-first flex-none rounded-full bg-indigo-400/10 px-2 py-1 text-xs font-medium text-indigo-400 ring-1 ring-indigo-400/30 ring-inset sm:order-0">*/}
                            {/*    Production*/}
                            {/*</div>*/}
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-1 bg-gray-950/70 sm:grid-cols-2 lg:grid-cols-3">
                            {stats.map((stat, statIdx) => (
                                <div
                                    key={stat.name}
                                    className={classNames(
                                        statIdx % 2 === 1 ? 'sm:border-l' : statIdx === 2 ? 'lg:border-l' : '',
                                        'border-t border-white/15 px-4 py-6 sm:px-6 lg:px-8',
                                    )}
                                >
                                    <p className="text-sm/6 font-medium text-gray-400">{stat.name}</p>
                                    <p className="mt-2 flex items-center gap-x-2">
                                    <span
                                        className="text-4xl font-semibold tracking-tight text-white">{stat.value}</span>
                                        {stat.verifyBtn && (stat.value ?? 0) > 0 && (
                                            <ButtonRounded
                                                className="bg-rose-500/80 text-xs px-3 ml-2"
                                                onClick={() => showVerifyModal()}
                                            >
                                                Zweryfikuj
                                            </ButtonRounded>
                                        )}
                                        {/*{stat.unit ? <span className="text-sm text-gray-400">{stat.unit}</span> : null}*/}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </header>
                }
                {contestAttemptsResult.isSuccess &&
                    <div className="border-t border-white/10 pt-11">
                        <h2 className="px-4 text-base/7 font-semibold text-white sm:px-6 lg:px-8">Ostatnie
                            aktywności</h2>
                        <table className="mt-6 w-full text-left whitespace-nowrap">
                            <colgroup>
                                <col className="w-full sm:w-4/12" />
                                <col className="lg:w-4/12" />
                                <col className="lg:w-2/12" />
                                <col className="lg:w-1/12" />
                                {/*<col className="lg:w-1/12" />*/}
                            </colgroup>
                            <thead className="border-b border-white/10 text-sm/6 text-white">
                            <tr>
                                <th scope="col" className="py-2 pr-8 pl-4 font-semibold sm:pl-6 lg:pl-8">
                                    ID uzytkownika
                                </th>
                                <th scope="col"
                                    className="py-2 pr-4 pl-0 text-right font-semibold sm:pr-8 sm:text-left lg:pr-20">
                                    Status
                                </th>
                                <th scope="col"
                                    className="py-2 pr-4 pl-0 text-right font-semibold sm:pr-8 sm:text-left lg:pr-20">
                                    Punkty
                                </th>
                                <th
                                    scope="col"
                                    className="hidden py-2 pr-4 pl-0 text-right font-semibold sm:table-cell sm:pr-6 lg:pr-8"
                                >
                                    Ostatnia aktywność
                                </th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                            {contestAttemptsResult.data?.data.data.length === 0 &&
                                <tr>
                                    <td colSpan={3}>
                                        <div
                                            className="truncate text-sm/6 font-medium text-neutral-300/70 text-center my-4">Brak
                                            elementow do wyswietlenia
                                        </div>
                                    </td>
                                </tr>
                            }

                            {contestAttemptsResult.data.data.data.map((contestAttempt) =>
                                <tr key={contestAttempt.id}
                                    role={'button'}
                                    className={'cursor-pointer'}
                                    onClick={() => showVerifyModal(contestAttempt.id)}
                                >
                                    <td className="py-4 pr-8 pl-4 sm:pl-6 lg:pl-8">
                                        <div className="flex items-center gap-x-4">
                                            <div
                                                className="truncate text-sm/6 font-medium text-white">{contestAttempt.user.customerId}</div>
                                        </div>
                                    </td>
                                    <td className="py-4 pr-4 pl-0 text-sm/6 sm:pr-8 lg:pr-20">
                                        <div className="flex items-center justify-end gap-x-2 sm:justify-start">
                                            <ContestAttemptStatusBadge status={contestAttempt.status} />
                                            {/*<div*/}
                                            {/*    className={classNames(statuses[contestAttempt.status == ContestAttemptStatusType.COMPLETED ? 'Completed' : 'Error'], 'flex-none rounded-full p-1')}>*/}
                                            {/*    <div className="size-1.5 rounded-full bg-current" />*/}
                                            {/*</div>*/}
                                            {/*<div*/}
                                            {/*    className="hidden text-white sm:block">{getStatusName(contestAttempt.status)}</div>*/}
                                        </div>
                                    </td>
                                    <td className="py-4 pr-8">
                                        <div className="flex items-center gap-x-4">
                                            <div
                                                className="truncate text-sm/6 font-medium text-white">
                                                {contestAttempt.score}/{contestAttempt.maxScore}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="hidden py-4 pr-4 pl-0 text-right text-sm/6 text-gray-400 sm:table-cell sm:pr-6 lg:pr-8">
                                        <time dateTime={contestAttempt.startedAt}>{contestAttempt.startedAt}</time>
                                    </td>
                                </tr>)
                            }

                            </tbody>
                        </table>
                        {/*<pre>*/}
                        {/*    {contestAttemptsResult.data?.data.data.length}*/}
                        {/*    {JSON.stringify(contestAttemptsResult)}*/}
                        {/*</pre>*/}
                        {/*{contestAttemptsResult.isSuccess && contestAttemptsResult.data?.data.data.length === 1 &&*/}
                        {/*    <div className={'text-center my-4 text-neutral-300/60'}>Brak elementow do wyswietlenia</div>*/}
                        {/*}*/}
                    </div>
                }
                {contestAttemptsResult.isLoading &&
                    <PulseLoader className={'mx-auto my-3'} description="Ładowanie ..." />
                }
            </main>
        </AccountLayout>
    );
}

export default AccountManagementContestsDetailPage;
